import React, { useEffect, useState } from "react";
import { adminClient } from "../../../lib/admin";
import { ShieldCheck, AlertTriangle, Check, Ban, Save } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  "Price OK": "bg-green-100 text-green-700",
  "Too High": "bg-orange-100 text-orange-700",
  "Too Low": "bg-amber-100 text-amber-700",
  "Profit Risk": "bg-red-100 text-red-700",
  "Needs Review": "bg-black/10 text-black/60",
  "Do Not Publish": "bg-red-600 text-white",
};
const APPROVAL_STYLE = { approved: "text-green-700", blocked: "text-red-600", pending: "text-amber-600" };
const m = (v) => (v === null || v === undefined ? "—" : `$${Number(v).toFixed(2)}`);

const ConfigPanel = ({ config, onSaved }) => {
  const pct = (v) => (v === null || v === undefined ? "" : Math.round(Number(v) * 100));
  const [profit, setProfit] = useState(pct(config?.min_profit_on_cost));
  const [margin, setMargin] = useState(pct(config?.min_margin));
  const [discount, setDiscount] = useState(pct(config?.market_discount));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await adminClient().put("/admin/price-guard/config", {
        min_profit_on_cost: profit === "" ? null : Number(profit) / 100,
        min_margin: margin === "" ? null : Number(margin) / 100,
        market_discount: discount === "" ? null : Number(discount) / 100,
      });
      toast.success("Global Price Guard targets updated.");
      onSaved?.(data.config);
    } catch { toast.error("Could not save targets."); } finally { setSaving(false); }
  };

  const inp = "w-16 border border-black/15 px-2 py-1.5 text-sm outline-none focus:border-taven-gold";
  return (
    <div className="border border-black/10 bg-taven-gray/40 p-4 mb-5" data-testid="pg-config-panel">
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-3">Global profit targets — applied to every product</p>
      <div className="flex flex-wrap items-end gap-4">
        <label className="text-sm">Min net profit
          <span className="block text-[11px] text-muted-foreground">% of item cost</span>
          <div className="flex items-center gap-1 mt-1"><input value={profit} onChange={(e) => setProfit(e.target.value)} type="number" className={inp} data-testid="pg-config-profit" /><span className="text-sm">%</span></div>
        </label>
        <label className="text-sm">Min margin
          <span className="block text-[11px] text-muted-foreground">% of sell price</span>
          <div className="flex items-center gap-1 mt-1"><input value={margin} onChange={(e) => setMargin(e.target.value)} type="number" className={inp} data-testid="pg-config-margin" /><span className="text-sm">%</span></div>
        </label>
        <label className="text-sm">Market discount
          <span className="block text-[11px] text-muted-foreground">target below market</span>
          <div className="flex items-center gap-1 mt-1"><input value={discount} onChange={(e) => setDiscount(e.target.value)} type="number" className={inp} data-testid="pg-config-discount" /><span className="text-sm">%</span></div>
        </label>
        <button onClick={save} disabled={saving} className="bg-taven-black text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-taven-orange" data-testid="pg-config-save">{saving ? "Saving…" : "Save Targets"}</button>
      </div>
    </div>
  );
};

const Row = ({ p, onSaved }) => {
  const [cost, setCost] = useState(p.cost ?? "");
  const [shipping, setShipping] = useState(p.shipping ?? "");
  const [ref, setRef] = useState(p.reference_market_price ?? "");
  const [entry, setEntry] = useState(p);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await adminClient().put(`/admin/price-guard/${p.handle}`, {
        cost: cost === "" ? null : Number(cost),
        shipping: shipping === "" ? null : Number(shipping),
        reference_market_price: ref === "" ? null : Number(ref),
      });
      setEntry(data); onSaved?.(data);
      toast.success("Price guard updated.");
    } catch { toast.error("Could not save."); } finally { setSaving(false); }
  };

  const setApproval = async (status) => {
    try {
      await adminClient().put(`/admin/price-guard/${p.handle}/approval`, { approval_status: status });
      setEntry((e) => ({ ...e, approval_status: status }));
      toast.success(`Marked ${status}.`);
    } catch { toast.error("Could not update approval."); }
  };

  const num = "w-20 border border-black/15 px-2 py-1.5 text-sm outline-none focus:border-taven-gold";
  return (
    <tr data-testid={`pg-row-${p.handle}`}>
      <td className="px-3 py-3">
        <div className="flex items-center gap-2 min-w-[180px]">
          {entry.image && <img src={entry.image} alt="" className="w-9 h-9 object-cover bg-taven-gray" />}
          <div><p className="text-sm font-semibold line-clamp-1">{entry.title}</p><p className="text-xs text-muted-foreground">{m(entry.price)} live</p></div>
        </div>
      </td>
      <td className="px-2 py-3"><input value={cost} onChange={(e) => setCost(e.target.value)} type="number" className={num} data-testid={`pg-cost-${p.handle}`} placeholder="cost" /></td>
      <td className="px-2 py-3"><input value={shipping} onChange={(e) => setShipping(e.target.value)} type="number" className={num} placeholder="ship" /></td>
      <td className="px-2 py-3"><input value={ref} onChange={(e) => setRef(e.target.value)} type="number" className={num} data-testid={`pg-ref-${p.handle}`} placeholder="market" /></td>
      <td className="px-2 py-3 text-sm">{m(entry.recommended_price)}</td>
      <td className="px-2 py-3 text-sm">{entry.profit === null ? "—" : <span className={entry.profit <= 0 ? "text-red-600 font-bold" : ""}>{m(entry.profit)}</span>}</td>
      <td className="px-2 py-3"><span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 whitespace-nowrap ${STATUS_STYLE[entry.status]}`} data-testid={`pg-status-${p.handle}`}>{entry.status}</span></td>
      <td className="px-2 py-3 text-xs font-bold uppercase capitalize"><span className={APPROVAL_STYLE[entry.approval_status]}>{entry.approval_status}</span></td>
      <td className="px-2 py-3">
        <div className="flex items-center gap-1.5">
          <button onClick={save} disabled={saving} className="p-1.5 bg-taven-black text-white hover:bg-taven-orange" title="Save & recompute" data-testid={`pg-save-${p.handle}`}><Save className="w-3.5 h-3.5" /></button>
          <button onClick={() => setApproval("approved")} className="p-1.5 bg-green-600 text-white hover:bg-green-700" title="Approve" data-testid={`pg-approve-${p.handle}`}><Check className="w-3.5 h-3.5" /></button>
          <button onClick={() => setApproval("blocked")} className="p-1.5 bg-red-600 text-white hover:bg-red-700" title="Block" data-testid={`pg-block-${p.handle}`}><Ban className="w-3.5 h-3.5" /></button>
        </div>
      </td>
    </tr>
  );
};

export default function AdminPriceGuard() {
  const [data, setData] = useState(null);
  const load = () => adminClient().get("/admin/price-guard?first=50").then(({ data }) => setData(data)).catch(() => setData({ products: [], summary: {} }));
  useEffect(() => { load(); }, []);
  if (!data) return <p className="text-sm text-muted-foreground">Loading products from TAVEN…</p>;

  const risky = (data.products || []).filter((p) => ["Do Not Publish", "Profit Risk", "Too Low"].includes(p.status));

  return (
    <div data-testid="admin-price-guard">
      <div className="flex items-center gap-2 mb-1"><ShieldCheck className="w-5 h-5 text-taven-gold" /><h1 className="text-2xl font-black font-display">TAVEN Price Guard</h1></div>
      <p className="text-sm text-muted-foreground mb-5">Enter cost, shipping and a market reference price per product. TAVEN recommends ~{Math.round((data.config?.market_discount||0.1)*100)}% below market when margin is safe. Products stay connected to the storefront — this is an advisory & approval layer, it does not change your live storefront.</p>

      <ConfigPanel config={data.config} onSaved={load} />

      <div className="flex flex-wrap gap-2 mb-5">
        {Object.entries(data.summary || {}).map(([s, n]) => (
          <div key={s} className="border border-black/10 bg-white px-3 py-2"><p className="text-[10px] uppercase tracking-wider text-muted-foreground">{s}</p><p className="text-lg font-black font-display">{n}</p></div>
        ))}
      </div>

      {risky.length > 0 && (
        <div className="bg-red-50 border border-red-200 text-red-800 text-sm p-4 mb-5" data-testid="pg-alerts">
          <div className="flex items-center gap-2 font-bold mb-2"><AlertTriangle className="w-4 h-4" /> {risky.length} product(s) need attention before publishing</div>
          <ul className="list-disc pl-5 space-y-1">
            {risky.slice(0, 8).map((p) => <li key={p.handle}><strong>{p.title}</strong> — {p.status}{p.alerts?.[0] ? `: ${p.alerts[0]}` : ""}</li>)}
          </ul>
        </div>
      )}

      <div className="border border-black/10 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-taven-gray/60 text-left"><tr>
            <th className="px-3 py-3 font-bold">Product</th><th className="px-2 py-3 font-bold">Cost</th>
            <th className="px-2 py-3 font-bold">Ship</th><th className="px-2 py-3 font-bold">Market</th>
            <th className="px-2 py-3 font-bold">Rec.</th><th className="px-2 py-3 font-bold">Profit</th>
            <th className="px-2 py-3 font-bold">Status</th><th className="px-2 py-3 font-bold">Approval</th>
            <th className="px-2 py-3 font-bold">Actions</th>
          </tr></thead>
          <tbody className="divide-y divide-black/5">
            {(data.products || []).map((p) => <Row key={p.handle} p={p} onSaved={load} />)}
            {(!data.products || data.products.length === 0) && <tr><td colSpan={9} className="px-4 py-6 text-center text-muted-foreground">No products found.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
